import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { trackEvent, trackReciprocity } from "../lib/track";
import MagneticButton from "../components/site/ui/MagneticButton";

const LOGO = "/assets/logo.png";

const getSessionId = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("session_id");
};

const Locked = ({ reason }) => (
  <div className="max-w-xl mx-auto text-center">
    <h1 className="font-serif text-4xl text-themed leading-tight font-light">
      Your Personal Action Plan is not yet unlocked.
    </h1>
    <p className="mt-6 text-themed-muted leading-relaxed">{reason}</p>
    <a href="/?checkout=blueprint" className="btn-ghost-gold mt-8 inline-block">Return to Personal Action Plan</a>
  </div>
);

const BlueprintStartPage = () => {
  const sessionId = getSessionId();
  const [state, setState] = useState({ status: "checking", reason: "" });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "locked", reason: "A purchase confirmation link is required." });
      return;
    }
    api.get(`/blueprint/submissions/check/${encodeURIComponent(sessionId)}`)
      .then((r) => {
        if (r.data?.submitted) {
          window.location.href = `/blueprint/preview?session_id=${encodeURIComponent(sessionId)}`;
          return;
        }
        setState({ status: "ready", reason: "" });
        trackEvent("blueprint_alignment_confirmation_viewed", { session_id: sessionId });
        trackReciprocity("blueprint_alignment_confirmation", {
          related_session_id: sessionId,
          related_product_type: "blueprint",
        });
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setState({
          status: "locked",
          reason: detail || "We could not verify this purchase yet. If your payment was completed, give it a moment and refresh. Your access is protected, not lost.",
        });
      });
  }, [sessionId]);

  return (
    <div data-testid="blueprint-start-page" className="min-h-screen bg-themed text-themed relative overflow-hidden flex items-center justify-center px-6">
      <div className="absolute inset-0 gold-glow-radial pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />
      <div className="relative max-w-2xl text-center py-20">
        <div className="flex justify-center mb-10">
          <img src={LOGO} alt="Authentic Momentum" className="w-24 h-24 object-contain logo-mask animate-breathe-premium" draggable="false" />
        </div>
        {state.status === "checking" && (
          <h1 className="font-serif text-4xl text-themed leading-tight font-light">Confirming your alignment…</h1>
        )}
        {state.status === "locked" && <Locked reason={state.reason} />}
        {state.status === "ready" && (
          <>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">First Alignment Point</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-themed leading-tight font-light">
              Your Personal Action Plan has opened.
            </h1>
            <p className="mt-6 text-themed-muted leading-relaxed">
              Your decision to begin is already a signal. You are no longer leaving your direction scattered in your mind. You are placing it into a clearer field of reflection.
            </p>
            <div className="mt-8 glass-card p-8 text-left">
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-3">This is the first alignment point</p>
              <p className="font-serif italic text-2xl text-themed leading-snug">
                You are ready to stop forcing clarity and start revealing it.
              </p>
            </div>
            <div className="mt-10 flex justify-center">
              <MagneticButton
                data-testid="blueprint-begin-questionnaire"
                onClick={() => {
                  window.location.href = `/blueprint/intake?session_id=${encodeURIComponent(sessionId)}`;
                }}
                className="btn-gold inline-flex items-center gap-2"
              >
                Begin the Questionnaire <ArrowRight size={13} />
              </MagneticButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BlueprintStartPage;
